import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp, stagger } from "@/lib/motion";
import { articles } from "@/lib/articles";

const SITE_URL = "https://psyloveyourself.com";

export default function ArticlesIndex() {
  const url = `${SITE_URL}/arthra`;

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Άρθρα · psyloveyourself",
    inLanguage: "el-GR",
    url,
    author: { "@type": "Person", name: "Άννα Παπαχρονόπουλου" },
    blogPost: articles.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      url: `${SITE_URL}/arthra/${a.slug}`,
      description: a.metaDescription,
      author: { "@type": "Person", name: "Άννα Παπαχρονόπουλου" },
    })),
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>Άρθρα Ψυχολογίας · Άννα Παπαχρονόπουλου | psyloveyourself</title>
        <meta
          name="description"
          content="Άρθρα ψυχολογίας στα ελληνικά: CBT, άγχος, OCD, online ψυχοθεραπεία και πρακτικές συμβουλές από ψυχολόγο στην Πάτρα."
        />
        <link rel="canonical" href={url} />
        <meta property="og:title" content="Άρθρα Ψυχολογίας · psyloveyourself" />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid="articles-index">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div {...fadeUp}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Αρχική
            </Link>
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-5">
              Άρθρα &amp; Γνώση
            </p>
            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[64px] leading-[1.04]">
              Επιστημονικά τεκμηριωμένη
              <br />
              <span className="italic font-light">ψυχολογία στα ελληνικά.</span>
            </h1>
            <p className="mt-6 font-body font-light text-lg text-foreground/70 max-w-[640px] leading-relaxed">
              Σύντομες, ξεκάθαρες αναγνώσεις πάνω σε ψυχοθεραπεία, άγχος, OCD,
              σχέσεις και αυτοεκτίμηση.
            </p>
          </motion.div>

          <motion.div
            {...stagger(0.08)}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {articles.map((a) => (
              <motion.div
                key={a.slug}
                variants={{
                  initial: { opacity: 0, y: 24 },
                  whileInView: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                  },
                }}
              >
                <Link
                  to={`/arthra/${a.slug}`}
                  data-testid={`articles-card-${a.slug}`}
                  className="group h-full block bg-card border border-border/60 rounded-3xl p-7 sm:p-8 transition-all duration-500 hover:-translate-y-1 hover:bg-background"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-[10px] tracking-[0.22em] uppercase font-body font-medium text-primary">
                      {a.tag}
                    </span>
                    <span className="text-muted-foreground text-xs">·</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-body text-muted-foreground">
                      <Clock size={12} strokeWidth={1.5} />
                      {a.readTime}
                    </span>
                  </div>
                  <h2 className="font-heading text-2xl sm:text-[26px] text-foreground tracking-tight leading-tight">
                    {a.title}
                  </h2>
                  <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed text-base">
                    {a.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-body text-foreground/80">
                    Διάβασέ το
                    <ArrowRight
                      size={14}
                      strokeWidth={1.5}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </main>

      <Footer />
    </>
  );
}
